/**
 * Embudo Mux paywall — eventos canónicos + taxonomía de fallos.
 *
 * Eventos de embudo:
 *   mux_limit_reached    → usuario llegó al tope de videos gratis
 *   mux_cta_viewed       → CTA/mensaje de compra visible
 *   mux_checkout_click   → clic en comprar
 *   mux_stripe_redirect  → redirect a Stripe/MP/checkout
 *
 * Eventos de diagnóstico:
 *   mux_cta_blocked      → CTA no pudo renderizarse
 *   mux_paywall_failure  → fallo clasificado (ver MuxPaywallFailureType)
 */

import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { DATAFAST_GOALS, trackDataFastGoal } from "@/lib/datafastAnalytics";
import { isPostHogConfigured } from "@/lib/posthogAnalytics";
import { resolveCheckoutCountryCode } from "@/lib/resolveCheckoutCountryCode";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";

export type MuxPaywallSurface =
  | "reels_to_buy"
  | "reels_feed_paywall"
  | "mux_video_player"
  | "bio_simple_video_overlay"
  | "product_reel"
  | "dual_cta_video_landing"
  | "gift_product"
  | "pack_page";

export type MuxPaywallTrigger =
  | "limit_reached_screen"
  | "inline_bottom_cta"
  | "paywall_panel"
  | "mux_cta_impression"
  | "video_overlay_open"
  | "page_mount";

/** Taxonomía canónica — cada valor debe aparecer en /admin/mux-conversion */
export type MuxPaywallFailureType =
  | "component_missing"
  | "playlist_empty"
  | "playlist_too_short"
  | "cta_not_visible"
  | "cta_hidden_below_fold"
  | "price_missing"
  | "price_invalid"
  | "price_load_failed"
  | "video_playback_failure"
  | "video_not_configured"
  | "limit_reached_no_cta"
  | "checkout_handler_missing"
  | "checkout_api_error"
  | "checkout_abandoned"
  | "stripe_abandoned"
  | "no_valid_cta_url_or_pack"
  | "stripe_not_configured"
  | "other";

export type MuxPaywallDiagnosisCategory =
  | "rendering"
  | "pricing"
  | "media"
  | "checkout"
  | "conversion";

export const MUX_PAYWALL_DIAGNOSIS_MATRIX: ReadonlyArray<{
  failure_type: MuxPaywallFailureType;
  category: MuxPaywallDiagnosisCategory;
  label: string;
  description: string;
  severity: "critical" | "high" | "medium" | "low";
  fix_hint: string;
}> = [
  {
    failure_type: "component_missing",
    category: "rendering",
    label: "Componente no montó",
    description: "ReelsToBuy u otro paywall no renderizó (producto sin media, error de carga).",
    severity: "critical",
    fix_hint: "Verificar API producto, video_url y que items.length > 0.",
  },
  {
    failure_type: "playlist_empty",
    category: "media",
    label: "Playlist vacía",
    description: "No hay videos ni imágenes en el reel — el usuario no puede ver contenido.",
    severity: "critical",
    fix_hint: "Configurar video_url o gallery en el producto / pack.",
  },
  {
    failure_type: "playlist_too_short",
    category: "media",
    label: "Playlist corta",
    description: "Menos items que maxFreeItems — el límite se alcanza antes de lo esperado.",
    severity: "medium",
    fix_hint: "Agregar más teasers o bajar maxFreeItems.",
  },
  {
    failure_type: "cta_not_visible",
    category: "rendering",
    label: "CTA no visible",
    description: "mux_cta_blocked — botón/mensaje de compra no se renderizó tras el límite.",
    severity: "critical",
    fix_hint: "Revisar z-index, showCTAButton, Stripe key y URL de CTA.",
  },
  {
    failure_type: "cta_hidden_below_fold",
    category: "rendering",
    label: "CTA bajo el fold",
    description: "Solo CTA inline (no pantalla full) — puede no verse en móvil.",
    severity: "high",
    fix_hint: "Forzar showPurchaseScreen o mejorar contraste del CTA inferior.",
  },
  {
    failure_type: "price_missing",
    category: "pricing",
    label: "Precio ausente",
    description: "CTA visible pero sin precio en pantalla.",
    severity: "high",
    fix_hint: "recommended_price en producto o pack pricing en Mux player.",
  },
  {
    failure_type: "price_invalid",
    category: "pricing",
    label: "Precio inválido",
    description: "Precio con undefined/null/NaN — rompe confianza y checkout.",
    severity: "high",
    fix_hint: "Validar API by-slug y getPackPricing por país.",
  },
  {
    failure_type: "price_load_failed",
    category: "pricing",
    label: "Error al cargar precio",
    description: "Falló fetch del producto o pricing por país.",
    severity: "critical",
    fix_hint: "Revisar /api/products/by-slug y country template prices.",
  },
  {
    failure_type: "video_playback_failure",
    category: "media",
    label: "Video no reproduce",
    description: "Mux player error — usuario no llega al límite con video roto.",
    severity: "high",
    fix_hint: "Playback ID, token signed, CORS, fallback IDs.",
  },
  {
    failure_type: "video_not_configured",
    category: "media",
    label: "Video no configurado",
    description: "VIP/card sin playback ID — salta directo a checkout sin teaser.",
    severity: "medium",
    fix_hint: "VITE_PREMIUM_MUX_* o admin premium-invite playback.",
  },
  {
    failure_type: "limit_reached_no_cta",
    category: "rendering",
    label: "Límite sin CTA",
    description: "mux_limit_reached sin mux_cta_viewed — gap de instrumentación o UI.",
    severity: "critical",
    fix_hint: "Revisar hasReachedLimit → trackMuxCtaViewed en ReelsToBuy.",
  },
  {
    failure_type: "checkout_handler_missing",
    category: "checkout",
    label: "Sin handler de compra",
    description: "Clic en CTA pero no hay onPurchaseClick ni purchaseUrl.",
    severity: "critical",
    fix_hint: "Pasar onPurchaseClick desde ProductReel/GiftProduct.",
  },
  {
    failure_type: "checkout_api_error",
    category: "checkout",
    label: "API checkout falló",
    description: "Stripe/MP session create devolvió error.",
    severity: "high",
    fix_hint: "Logs create-subscription-checkout, keys, price IDs.",
  },
  {
    failure_type: "checkout_abandoned",
    category: "checkout",
    label: "Checkout abandonado",
    description: "mux_checkout_click sin mux_stripe_redirect.",
    severity: "medium",
    fix_hint: "IG webview, errores JS, redirect bloqueado.",
  },
  {
    failure_type: "stripe_abandoned",
    category: "conversion",
    label: "Stripe sin pago",
    description: "Redirect a Stripe/MP pero sin payment_completed.",
    severity: "medium",
    fix_hint: "Precio alto, fricción checkout, recovery emails.",
  },
  {
    failure_type: "no_valid_cta_url_or_pack",
    category: "rendering",
    label: "Sin URL ni pack",
    description: "MuxVideoPlayer sin ctaUrl válida ni overridePackId.",
    severity: "critical",
    fix_hint: "Worker CTA, staticPageVideoConfig, overridePackId.",
  },
  {
    failure_type: "stripe_not_configured",
    category: "checkout",
    label: "Stripe no configurado",
    description: "Pack definido pero sin VITE_STRIPE_PUBLISHABLE_KEY.",
    severity: "critical",
    fix_hint: "Env Stripe en build / brand-runtime-config.",
  },
  {
    failure_type: "other",
    category: "conversion",
    label: "Otro",
    description: "Fallo no clasificado — ver failure_reason en el evento.",
    severity: "low",
    fix_hint: "Inspeccionar PostHog session replay.",
  },
];

function failureCategory(
  type: MuxPaywallFailureType,
): MuxPaywallDiagnosisCategory {
  return (
    MUX_PAYWALL_DIAGNOSIS_MATRIX.find((m) => m.failure_type === type)?.category ??
    "conversion"
  );
}

type Scalar = string | number | boolean | null;

export type MuxPaywallContext = {
  surface: MuxPaywallSurface;
  trigger?: MuxPaywallTrigger;
  playbackId?: string;
  videoTitle?: string;
  priceShown?: string;
  priceAmount?: number;
  priceCurrency?: string;
  limitReachedCount?: number;
  maxFreeItems?: number;
  productId?: string;
  productSlug?: string;
  packName?: string;
  gateway?: "stripe" | "mercadopago" | "internal_checkout" | "external_url";
  failureReason?: string;
  failureType?: MuxPaywallFailureType;
  priceValid?: boolean;
  videoIndex?: number;
  itemsCount?: number;
  componentName?: string;
  extra?: Record<string, Scalar>;
};

function baseContext(): Record<string, Scalar> {
  if (typeof window === "undefined") return { site: BRAND_SOURCE_KEY };
  const cfg = getServerTemplateConfig();
  const country = resolveCheckoutCountryCode(cfg?.country);
  return {
    site: BRAND_SOURCE_KEY,
    country: country === "UNKNOWN" ? null : country,
    template: cfg?.templateName ?? null,
    worker_country: cfg?.country ?? null,
    page_path: window.location.pathname,
  };
}

function flattenContext(ctx: MuxPaywallContext): Record<string, Scalar> {
  const priceValid =
    ctx.priceValid ??
    (ctx.priceAmount != null
      ? Number.isFinite(ctx.priceAmount) && ctx.priceAmount > 0
      : ctx.priceShown != null &&
        ctx.priceShown.trim() !== "" &&
        !/undefined|null|NaN/i.test(ctx.priceShown));

  const failureType =
    ctx.failureType ??
    (priceValid === false && ctx.priceShown
      ? "price_invalid"
      : priceValid === false
        ? "price_missing"
        : null);

  return {
    ...baseContext(),
    flow_surface: ctx.surface,
    ...(ctx.trigger ? { trigger: ctx.trigger } : {}),
    ...(ctx.playbackId
      ? {
          playback_id: ctx.playbackId,
          playback_id_short: ctx.playbackId.slice(-8),
          video_id: ctx.playbackId,
        }
      : {}),
    ...(ctx.videoTitle ? { video_title: ctx.videoTitle.slice(0, 120) } : {}),
    ...(ctx.priceShown ? { price_shown: ctx.priceShown.slice(0, 64) } : {}),
    ...(ctx.priceAmount != null ? { price_amount: ctx.priceAmount } : {}),
    ...(ctx.priceCurrency ? { price_currency: ctx.priceCurrency } : {}),
    price_valid: priceValid,
    ...(ctx.limitReachedCount != null
      ? { limit_reached_count: ctx.limitReachedCount }
      : {}),
    ...(ctx.maxFreeItems != null ? { max_free_items: ctx.maxFreeItems } : {}),
    ...(ctx.itemsCount != null ? { items_count: ctx.itemsCount } : {}),
    ...(ctx.productId ? { product_id: ctx.productId } : {}),
    ...(ctx.productSlug ? { product_slug: ctx.productSlug } : {}),
    ...(ctx.packName ? { pack_name: ctx.packName.slice(0, 80) } : {}),
    ...(ctx.gateway ? { checkout_gateway: ctx.gateway } : {}),
    ...(ctx.failureReason
      ? { failure_reason: ctx.failureReason.slice(0, 200) }
      : {}),
    ...(failureType
      ? {
          failure_type: failureType,
          diagnosis_category: failureCategory(failureType),
        }
      : {}),
    ...(ctx.componentName ? { component: ctx.componentName } : {}),
    ...(ctx.videoIndex != null ? { video_index: ctx.videoIndex } : {}),
    cta_visible: ctx.extra?.cta_visible === false ? false : true,
    ...ctx.extra,
  };
}

function capture(event: string, props: Record<string, Scalar>): void {
  if (!isPostHogConfigured()) return;
  try {
    const withSource = {
      source: BRAND_SOURCE_KEY,
      ...props,
    };
    window.posthog.capture(event, withSource);
  } catch {
    /* best-effort */
  }
}

const ctaViewedDedup = new Set<string>();
const limitReachedDedup = new Set<string>();
const failureDedup = new Set<string>();

export function trackMuxLimitReached(ctx: MuxPaywallContext): void {
  const dedupeKey = [
    ctx.surface,
    ctx.playbackId ?? "",
    String(ctx.limitReachedCount ?? ""),
  ].join("|");
  if (limitReachedDedup.has(dedupeKey)) return;
  limitReachedDedup.add(dedupeKey);

  const payload = flattenContext(ctx);
  capture("mux_limit_reached", payload);
  capture("conversion_funnel_step", {
    funnel_step: "mux_limit_reached",
    converted: false,
    ...payload,
  });
}

export function trackMuxCtaViewed(ctx: MuxPaywallContext): void {
  const dedupeKey = [
    ctx.surface,
    ctx.trigger ?? "",
    ctx.playbackId ?? "",
    String(ctx.limitReachedCount ?? ""),
    String(ctx.videoIndex ?? ""),
  ].join("|");
  if (ctaViewedDedup.has(dedupeKey)) return;
  ctaViewedDedup.add(dedupeKey);

  const priceValid =
    ctx.priceValid ??
    (ctx.priceShown != null &&
      ctx.priceShown.trim() !== "" &&
      !/undefined|null|NaN/i.test(ctx.priceShown));

  const payload = flattenContext({
    ...ctx,
    priceValid,
    failureType:
      !priceValid && !ctx.priceShown?.trim()
        ? "price_missing"
        : !priceValid
          ? "price_invalid"
          : ctx.failureType,
  });
  capture("mux_cta_viewed", payload);
  capture("conversion_funnel_step", {
    funnel_step: "mux_cta_viewed",
    converted: false,
    ...payload,
  });
}

export function trackMuxCheckoutClick(ctx: MuxPaywallContext): void {
  const payload = flattenContext(ctx);
  capture("mux_checkout_click", { ...payload, converted: false });
  capture("conversion_funnel_step", {
    funnel_step: "mux_checkout_click",
    converted: false,
    ...payload,
  });
  // DataFast: Stripe-only (never MercadoPago)
  if (!ctx.gateway || ctx.gateway === "stripe") {
    trackDataFastGoal(DATAFAST_GOALS.muxCtaClick, {
      surface: ctx.surface,
      gateway: ctx.gateway || "stripe",
    });
  }
}

export function trackMuxStripeRedirect(
  ctx: MuxPaywallContext & { redirectUrl?: string },
): void {
  const payload = flattenContext(ctx);
  if (ctx.redirectUrl) {
    payload.redirect_host = (() => {
      try {
        return new URL(ctx.redirectUrl).hostname;
      } catch {
        return null;
      }
    })();
  }
  capture("mux_stripe_redirect", payload);
  capture("conversion_funnel_step", {
    funnel_step: "mux_stripe_redirect",
    ...payload,
  });
  // DataFast initiate_checkout — Stripe paths only (MP out of scope)
  if (!ctx.gateway || ctx.gateway === "stripe") {
    trackDataFastGoal(DATAFAST_GOALS.initiateCheckout, {
      surface: ctx.surface,
      gateway: "stripe",
      flow: "mux_paywall",
    });
  }
}

const MUX_CTA_BLOCKED_ALIAS_TYPES = new Set<MuxPaywallFailureType>([
  "cta_not_visible",
  "no_valid_cta_url_or_pack",
  "cta_hidden_below_fold",
  "limit_reached_no_cta",
  "stripe_not_configured",
]);

/** Fallo clasificado — un solo evento para el mapa de diagnóstico. */
export function trackMuxPaywallFailure(
  ctx: MuxPaywallContext & { failureType: MuxPaywallFailureType },
): void {
  const dedupeKey = [
    ctx.surface,
    ctx.failureType,
    ctx.playbackId ?? "",
    ctx.productSlug ?? "",
    ctx.page_path ?? (typeof window !== "undefined" ? window.location.pathname : ""),
  ].join("|");
  if (failureDedup.has(dedupeKey)) return;
  failureDedup.add(dedupeKey);

  const payload = flattenContext({
    ...ctx,
    failureReason: ctx.failureReason ?? ctx.failureType,
    extra: { ...ctx.extra, cta_visible: false },
  });
  capture("mux_paywall_failure", payload);
  if (MUX_CTA_BLOCKED_ALIAS_TYPES.has(ctx.failureType)) {
    capture("mux_cta_blocked", payload);
  }
}

/** @deprecated Usar trackMuxPaywallFailure con failureType cta_not_visible */
export function trackMuxCtaBlocked(
  ctx: MuxPaywallContext & { blockReason: string },
): void {
  const mapped: MuxPaywallFailureType =
    ctx.blockReason === "no_valid_cta_url_or_pack"
      ? "no_valid_cta_url_or_pack"
      : ctx.blockReason === "stripe_not_configured"
        ? "stripe_not_configured"
        : "cta_not_visible";

  trackMuxPaywallFailure({
    ...ctx,
    failureType: mapped,
    failureReason: ctx.blockReason,
  });
}

/** Conversión por precio — para detectar “precio caro” (bajo CTR por price_amount). */
export function isPriceValid(
  priceShown?: string,
  priceAmount?: number,
): boolean {
  if (priceAmount != null) {
    return Number.isFinite(priceAmount) && priceAmount > 0;
  }
  return (
    priceShown != null &&
    priceShown.trim() !== "" &&
    !/undefined|null|NaN/i.test(priceShown)
  );
}